import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { apiFetch, ApiError } from '@/lib/api-client';
import { VerifyQrResponseSchema, type VerifyQrResponse } from '@formacion-docente/schemas';
import { Button } from '@/components/ui/button';
import { mapApiError } from '@/lib/error-map';

export function VerCredencialPage() {
  const [params] = useSearchParams();
  const token = params.get('t');
  const [data, setData] = useState<VerifyQrResponse | null>(null);
  const [error, setError] = useState<string | undefined>();
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    if (!token) {
      setError('Falta el token en la URL');
      setLoading(false);
      return;
    }
    apiFetch(`/auth/verify-qr?t=${encodeURIComponent(token)}`, VerifyQrResponseSchema)
      .then((r) => {
        if (cancelled) return;
        setData(r);
        setLoading(false);
      })
      .catch((e) => {
        if (cancelled) return;
        if (e instanceof ApiError && e.status === 410) {
          setError('El enlace ha expirado o ya fue utilizado.');
        } else {
          setError(mapApiError(e));
        }
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [token]);

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-bg text-text-secondary">
        Verificando…
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-bg p-4">
        <div className="w-full max-w-sm border border-border bg-bg-elevated p-6 text-center">
          <h1 className="text-xl font-bold text-destructive">Enlace no válido</h1>
          <p className="mt-2 text-sm text-text-secondary">{error}</p>
          <a href="/publica" className="mt-4 inline-block text-sm text-primary hover:underline">
            ← Volver a consultar
          </a>
        </div>
      </div>
    );
  }

  if (!data) return null;

  return (
    <div className="flex min-h-screen items-center justify-center bg-bg p-4">
      <div className="w-full max-w-sm space-y-4 border border-border bg-bg-elevated p-6 text-center">
        <h1 className="text-xl font-bold">Tu credencial</h1>
        <img
          src={`data:image/png;base64,${data.qr_png_base64}`}
          alt="Código QR de la credencial"
          className="mx-auto h-48 w-48"
        />
        <p className="text-xs text-text-secondary">Este enlace es de un solo uso.</p>
        <div className="flex flex-col gap-2">
          <Button asChild>
            <a href={data.pdf_url} target="_blank" rel="noopener noreferrer">
              Descargar PDF
            </a>
          </Button>
          <Button variant="outline" asChild>
            <a href={`data:image/png;base64,${data.qr_png_base64}`} download="credencial.png">
              Guardar PNG
            </a>
          </Button>
        </div>
      </div>
    </div>
  );
}